import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Calculator, DollarSign, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

// ——— 2026 NM Pricing Data ———

const ROOFING_MATERIAL_COST: Record<string, { low: number; high: number; label: string }> = {
  "3-tab": { low: 3.50, high: 5.50, label: "3-Tab Shingles" },
  architectural: { low: 4.50, high: 7.00, label: "Architectural Shingles" },
  designer: { low: 6.00, high: 10.00, label: "Premium / Designer Shingles" },
  metal: { low: 7.00, high: 14.00, label: "Metal Roofing" },
  tile: { low: 8.00, high: 15.00, label: "Tile (Clay / Concrete)" },
  flat: { low: 4.00, high: 8.00, label: "Flat / TPO / EPDM" },
};

const STORY_MULTIPLIER: Record<string, number> = { "1": 1.0, "2": 1.15, "3": 1.30 };
const TEAROFF_COST = { low: 1.0, high: 2.0 };
const ROOF_COMPLEXITY: Record<string, number> = { simple: 1.0, moderate: 1.10, complex: 1.25 };

// Solar 2026 NM — only NM state credit applies
const SOLAR_COST_PER_WATT = { low: 2.50, high: 3.20 };
const SOLAR_NM_CREDIT_RATE = 0.10;
const SOLAR_NM_CREDIT_CAP = 6000;
const NM_ELECTRICITY_RATE = 0.13;
const SOLAR_PRODUCTION_FACTOR = 1600;

// Construction 2026
const CONSTRUCTION_COSTS: Record<string, { low: number; high: number; label: string }> = {
  kitchen: { low: 150, high: 350, label: "Kitchen Remodel" },
  bathroom: { low: 200, high: 450, label: "Bathroom Remodel" },
  addition: { low: 150, high: 350, label: "Room Addition" },
  general: { low: 100, high: 250, label: "General Remodel" },
  garage: { low: 80, high: 150, label: "Garage / Carport" },
};

const CONSTRUCTION_QUALITY: Record<string, number> = { budget: 0.75, mid: 1.0, "high-end": 1.35 };

// Mold 2026
const MOLD_COSTS: Record<string, { low: number; high: number; label: string }> = {
  small: { low: 1500, high: 3500, label: "Small Area (< 50 sq ft)" },
  medium: { low: 3500, high: 10000, label: "Medium Area (50–300 sq ft)" },
  large: { low: 10000, high: 25000, label: "Large Area (300+ sq ft)" },
  crawl: { low: 3000, high: 8000, label: "Crawl Space" },
  wholehouse: { low: 10000, high: 30000, label: "Whole House" },
};

const MOLD_SEVERITY: Record<string, number> = { minor: 0.85, moderate: 1.0, severe: 1.30 };
const MOLD_WATER: Record<string, number> = { no: 1.0, yes: 1.25 };

type ServiceType = "roofing" | "solar" | "construction" | "mold";

interface EstimateResult {
  low: number;
  high: number;
  notes: string[];
  disclaimer: string;
}

function calcRoofing(data: Record<string, string>): EstimateResult {
  const sqft = parseInt(data.sqft) || 1700;
  const material = ROOFING_MATERIAL_COST[data.material] || ROOFING_MATERIAL_COST.architectural;
  const stories = STORY_MULTIPLIER[data.stories] || 1.0;
  const complexity = ROOF_COMPLEXITY[data.complexity] || 1.0;
  const needsTearoff = data.tearoff === "yes";

  let low = sqft * material.low * stories * complexity;
  let high = sqft * material.high * stories * complexity;

  if (needsTearoff) {
    low += sqft * TEAROFF_COST.low;
    high += sqft * TEAROFF_COST.high;
  }

  const notes: string[] = [
    `${material.label} on ~${sqft.toLocaleString()} sq ft roof`,
    `${data.stories || "1"}-story home, ${data.complexity || "moderate"} roof complexity`,
    needsTearoff ? "Includes tear-off of existing roof" : "Overlay (no tear-off)",
  ];

  return {
    low: Math.round(low * 0.90),
    high: Math.round(high * 1.10),
    notes,
    disclaimer: "Actual pricing may vary ±15% based on decking condition, permit fees, flashing needs, and specific product selections.",
  };
}

function calcSolar(data: Record<string, string>): EstimateResult {
  const monthlyBill = parseInt(data.monthlyBill) || 150;
  const monthlyKwh = monthlyBill / NM_ELECTRICITY_RATE;
  const annualKwh = monthlyKwh * 12;
  const offsetPct = parseInt(data.offset) || 100;
  const targetKwh = annualKwh * (offsetPct / 100);
  const systemKw = targetKwh / SOLAR_PRODUCTION_FACTOR;
  const systemWatts = systemKw * 1000;

  const grossLow = systemWatts * SOLAR_COST_PER_WATT.low;
  const grossHigh = systemWatts * SOLAR_COST_PER_WATT.high;

  const nmCreditLow = Math.min(grossLow * SOLAR_NM_CREDIT_RATE, SOLAR_NM_CREDIT_CAP);
  const nmCreditHigh = Math.min(grossHigh * SOLAR_NM_CREDIT_RATE, SOLAR_NM_CREDIT_CAP);

  const netLow = grossLow - nmCreditLow;
  const netHigh = grossHigh - nmCreditHigh;

  const annualSavings = targetKwh * NM_ELECTRICITY_RATE;
  const paybackLow = netLow / annualSavings;
  const paybackHigh = netHigh / annualSavings;

  const roofNote = data.roofAge === "old" 
    ? "⚠️ Your roof may need replacement before solar installation" 
    : "";

  const notes = [
    `Estimated system size: ${systemKw.toFixed(1)} kW (${offsetPct}% offset)`,
    `Gross cost: $${Math.round(grossLow).toLocaleString()} – $${Math.round(grossHigh).toLocaleString()}`,
    `NM State Credit (10%, max $6K): -$${Math.round(nmCreditLow).toLocaleString()} to -$${Math.round(nmCreditHigh).toLocaleString()}`,
    `Est. annual savings: ~$${Math.round(annualSavings).toLocaleString()}/yr`,
    `Payback period: ~${paybackLow.toFixed(1)} – ${paybackHigh.toFixed(1)} years`,
    ...(roofNote ? [roofNote] : []),
  ];

  return {
    low: Math.round(netLow * 0.92),
    high: Math.round(netHigh * 1.08),
    notes,
    disclaimer: "Net cost shown after NM state tax credit. The federal 30% ITC is no longer available. Actual pricing varies by roof orientation, shading, panel brand, and installer. Incentives subject to eligibility.",
  };
}

function calcConstruction(data: Record<string, string>): EstimateResult {
  const sqft = parseInt(data.sqft) || 200;
  const type = CONSTRUCTION_COSTS[data.projectType] || CONSTRUCTION_COSTS.general;
  const quality = CONSTRUCTION_QUALITY[data.quality] || 1.0;

  const low = sqft * type.low * quality;
  const high = sqft * type.high * quality;

  const notes = [
    `${type.label}, ~${sqft.toLocaleString()} sq ft`,
    `Quality level: ${data.quality || "mid"}-range finishes`,
    data.permits === "yes" ? "Includes estimated permit costs" : "Permits may be additional",
  ];

  return {
    low: Math.round(low * 0.85),
    high: Math.round(high * 1.15),
    notes,
    disclaimer: "Construction estimates vary ±20% based on structural changes, material selections, site conditions, and permit requirements.",
  };
}

function calcMold(data: Record<string, string>): EstimateResult {
  const area = MOLD_COSTS[data.area] || MOLD_COSTS.medium;
  const severity = MOLD_SEVERITY[data.severity] || 1.0;
  const water = MOLD_WATER[data.waterDamage] || 1.0;

  const low = area.low * severity * water;
  const high = area.high * severity * water;

  const notes = [
    `${area.label}`,
    `Severity: ${data.severity || "moderate"}`,
    data.waterDamage === "yes" ? "Includes water damage remediation" : "No active water damage",
    data.testing === "yes" ? "Post-remediation testing recommended (~$300–$600 additional)" : "",
  ].filter(Boolean);

  return {
    low: Math.round(low * 0.90),
    high: Math.round(high * 1.10),
    notes,
    disclaimer: "Mold remediation costs vary ±15% based on mold type, accessibility, containment needs, and extent of structural damage.",
  };
}

interface StepField {
  key: string;
  label: string;
  type: "select" | "number";
  placeholder?: string;
  options?: { value: string; label: string }[];
  suffix?: string;
}

const STEPS: Record<ServiceType, StepField[][]> = {
  roofing: [
    [
      { key: "sqft", label: "Approximate roof area (sq ft)", type: "number", placeholder: "e.g. 1700" },
      { key: "material", label: "Roofing material", type: "select", options: Object.entries(ROOFING_MATERIAL_COST).map(([v, d]) => ({ value: v, label: d.label })) },
    ],
    [
      { key: "stories", label: "Number of stories", type: "select", options: [{ value: "1", label: "1 Story" }, { value: "2", label: "2 Stories" }, { value: "3", label: "3+ Stories" }] },
      { key: "complexity", label: "Roof complexity", type: "select", options: [{ value: "simple", label: "Simple (few angles)" }, { value: "moderate", label: "Moderate (typical)" }, { value: "complex", label: "Complex (many angles, valleys)" }] },
    ],
    [
      { key: "tearoff", label: "Does the old roof need to be torn off?", type: "select", options: [{ value: "yes", label: "Yes, tear off existing" }, { value: "no", label: "No, overlay is fine" }, { value: "unsure", label: "Not sure" }] },
    ],
  ],
  solar: [
    [
      { key: "monthlyBill", label: "Average monthly electric bill ($)", type: "number", placeholder: "e.g. 150", suffix: "/mo" },
    ],
    [
      { key: "offset", label: "How much of your bill do you want to offset?", type: "select", options: [{ value: "100", label: "100% (Full offset)" }, { value: "80", label: "80%" }, { value: "60", label: "60%" }, { value: "50", label: "50%" }] },
      { key: "roofAge", label: "Approximate roof age", type: "select", options: [{ value: "new", label: "Less than 5 years" }, { value: "mid", label: "5–15 years" }, { value: "old", label: "15+ years (may need replacement)" }] },
    ],
  ],
  construction: [
    [
      { key: "projectType", label: "Type of project", type: "select", options: Object.entries(CONSTRUCTION_COSTS).map(([v, d]) => ({ value: v, label: d.label })) },
      { key: "sqft", label: "Approximate project area (sq ft)", type: "number", placeholder: "e.g. 200" },
    ],
    [
      { key: "quality", label: "Finish quality level", type: "select", options: [{ value: "budget", label: "Budget-friendly" }, { value: "mid", label: "Mid-range" }, { value: "high-end", label: "High-end / Custom" }] },
      { key: "permits", label: "Will permits be needed?", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "unsure", label: "Not sure" }] },
    ],
  ],
  mold: [
    [
      { key: "area", label: "Affected area", type: "select", options: Object.entries(MOLD_COSTS).map(([v, d]) => ({ value: v, label: d.label })) },
      { key: "severity", label: "Severity level", type: "select", options: [{ value: "minor", label: "Minor (surface mold)" }, { value: "moderate", label: "Moderate (spreading)" }, { value: "severe", label: "Severe (structural impact)" }] },
    ],
    [
      { key: "waterDamage", label: "Is there active water damage?", type: "select", options: [{ value: "no", label: "No" }, { value: "yes", label: "Yes" }] },
      { key: "testing", label: "Do you need post-remediation testing?", type: "select", options: [{ value: "yes", label: "Yes (recommended)" }, { value: "no", label: "No" }] },
    ],
  ],
};

const SERVICE_LABELS: Record<ServiceType, string> = {
  roofing: "Roofing",
  solar: "Solar",
  construction: "Construction",
  mold: "Mold & Water Mitigation",
};

interface EstimateCalculatorProps {
  defaultService?: ServiceType;
  compact?: boolean;
}

const EstimateCalculator = ({ defaultService, compact = false }: EstimateCalculatorProps) => {
  const [service, setService] = useState<ServiceType | "">(defaultService || "");
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [result, setResult] = useState<EstimateResult | null>(null);

  const steps = service ? STEPS[service] : [];
  const totalSteps = steps.length;

  const reset = () => {
    setService(defaultService || "");
    setStep(0);
    setFormData({});
    setResult(null);
  };

  const updateField = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    if (!service) return false;
    if (step >= totalSteps) return false;
    const currentFields = steps[step];
    return currentFields.every((f) => {
      const val = formData[f.key];
      if (f.type === "number") return val && parseInt(val) > 0;
      return !!val;
    });
  };

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      let res: EstimateResult;
      switch (service) {
        case "roofing":
          if (formData.tearoff === "unsure") formData.tearoff = "yes";
          res = calcRoofing(formData);
          break;
        case "solar":
          res = calcSolar(formData);
          break;
        case "construction":
          res = calcConstruction(formData);
          break;
        case "mold":
          res = calcMold(formData);
          break;
        default:
          return;
      }
      setResult(res);
    }
  };

  return (
    <div className={`bg-card border rounded-2xl shadow-lg overflow-hidden ${compact ? "" : ""}`}>
      {/* Header */}
      <div className="bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
            <Calculator className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-black text-primary-foreground">Instant Estimate Calculator</h3>
            <p className="text-xs text-primary-foreground/70">2026 NM pricing • No commitment required</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          {/* Result View */}
          {result ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-5"
            >
              <div className="text-center">
                <p className="text-sm font-bold uppercase tracking-widest text-primary mb-2">
                  Your {SERVICE_LABELS[service as ServiceType]} Estimate
                </p>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <DollarSign className="h-7 w-7 text-primary" />
                  <span className="text-3xl md:text-4xl font-black text-foreground">
                    ${result.low.toLocaleString()} – ${result.high.toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Estimated range based on your inputs</p>
              </div>

              <div className="bg-secondary/50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-bold text-foreground flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-primary" /> Estimate Breakdown
                </p>
                {result.notes.map((note, i) => (
                  <p key={i} className="text-sm text-muted-foreground pl-5">• {note}</p>
                ))}
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-foreground mb-1">Important Note</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{result.disclaimer}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href="tel:5052640170" className="flex-1">
                  <Button size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-12 gap-2">
                    Get Exact Quote — Call Now
                  </Button>
                </a>
                <Button size="lg" variant="outline" onClick={reset} className="font-bold h-12 text-foreground hover:text-foreground">
                  Start Over
                </Button>
              </div>

              <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
                This is an automated estimate using average 2026 NM market pricing and is not a binding quote.
                Contact us for a free on-site inspection and precise quote.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key={`step-${step}-${service}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {/* Service Selection */}
              {!service ? (
                <div className="space-y-3">
                  <p className="text-sm font-bold text-foreground">What type of project do you need an estimate for?</p>
                  <div className="grid grid-cols-2 gap-3">
                    {(Object.entries(SERVICE_LABELS) as [ServiceType, string][]).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => setService(key)}
                        className="text-left p-4 rounded-xl border-2 border-border hover:border-primary bg-background hover:bg-primary/5 transition-all group"
                      >
                        <p className="font-bold text-foreground group-hover:text-primary text-sm">{label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {/* Progress */}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-widest text-primary">
                      Step {step + 1} of {totalSteps}
                    </p>
                    <div className="flex gap-1.5">
                      {Array.from({ length: totalSteps }).map((_, i) => (
                        <div
                          key={i}
                          className={`h-1.5 rounded-full transition-all ${i <= step ? "bg-primary w-6" : "bg-border w-4"}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="space-y-4">
                    {steps[step].map((field) => (
                      <div key={field.key} className="space-y-1.5">
                        <Label className="text-sm font-medium text-foreground">{field.label}</Label>
                        {field.type === "select" ? (
                          <Select value={formData[field.key] || ""} onValueChange={(v) => updateField(field.key, v)}>
                            <SelectTrigger className="h-12">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type="number"
                            min={1}
                            placeholder={field.placeholder}
                            value={formData[field.key] || ""}
                            onChange={(e) => updateField(field.key, e.target.value)}
                            className="h-12"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Navigation */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (step > 0) setStep(step - 1);
                        else { setService(defaultService || ""); setFormData({}); }
                      }}
                      className="font-bold h-11 gap-1.5 text-foreground hover:text-foreground"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </Button>
                    <Button
                      onClick={handleNext}
                      disabled={!canProceed()}
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-11 gap-1.5"
                    >
                      {step < totalSteps - 1 ? "Next" : "Calculate Estimate"} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info footer */}
        {!result && (
          <div className="mt-4 pt-3 border-t flex items-start gap-2">
            <Info className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Uses average 2026 New Mexico market pricing with built-in variance to account for unknowns.
              For an exact quote, call us for a free inspection.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EstimateCalculator;
